import { useListState } from "@/hooks/use-list-state";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ExternalLink, Loader2, RefreshCw, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { MarketplaceFeesResponse, VendorMarketplaceStatusRow } from "./types";

export function MarketplaceFeesPanel() {
  const [statusFilter, setStatusFilter] = useListState<"all" | "opted-in" | "listed">("marketplace-fees:status", "all");
  const [vendorSearch, setVendorSearch] = useListState("marketplace-fees:vendors", "");
  const [feeSearch, setFeeSearch] = useListState("marketplace-fees:fees", "");

  const feesQuery = useQuery<MarketplaceFeesResponse>({
    queryKey: ["/api/app/admin/marketplace-fees"],
  });
  const vendorStatusQuery = useQuery<VendorMarketplaceStatusRow[]>({
    queryKey: ["/api/app/admin/vendor-marketplace-status"],
  });

  const fees = feesQuery.data?.fees ?? [];
  const monthlyAggregates = feesQuery.data?.monthlyAggregates ?? [];
  const totalCents = feesQuery.data?.totalCents ?? 0;

  const vendors = vendorStatusQuery.data ?? [];

  const filteredVendors = vendors.filter((v) => {
    if (statusFilter === "opted-in" && !v.marketplaceOptedIn) return false;
    if (statusFilter === "listed" && !v.isMarketplaceListed) return false;
    if (vendorSearch.trim()) {
      const q = vendorSearch.toLowerCase();
      const nameMatch = String(v.name ?? "").toLowerCase().includes(q);
      const bizMatch = v.businessName?.toLowerCase().includes(q) ?? false;
      if (!nameMatch && !bizMatch) return false;
    }
    return true;
  });

  const filteredFees = fees.filter((f) =>
    feeSearch.trim() === "" || String(f.vendorName ?? "").toLowerCase().includes(feeSearch.toLowerCase()) || f.monthKey.includes(feeSearch.trim()),
  );

  const optedInCount = vendors.filter((v) => v.marketplaceOptedIn).length;
  const listedCount = vendors.filter((v) => v.isMarketplaceListed).length;

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-tour="admin-vendor-marketplace-fees-summary">
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">Total Vendors</p>
          <p className="text-2xl font-bold" data-testid="text-marketplace-total-vendors">{vendors.length}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">Opted In</p>
          <p className="text-2xl font-bold text-brand" data-testid="text-marketplace-opted-in">{optedInCount}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">Listed</p>
          <p className="text-2xl font-bold" data-testid="text-marketplace-listed">{listedCount}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">All-time Revenue</p>
          <p className="text-2xl font-bold text-green-600" data-testid="text-marketplace-total-revenue">
            ${(totalCents / 100).toFixed(2)}
          </p>
        </Card>
      </div>

      {/* Monthly aggregates */}
      {monthlyAggregates.length > 0 && (
        <Card className="p-4">
          <h2 className="font-semibold text-base mb-3">Monthly Revenue</h2>
          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase">
                <tr>
                  <th className="text-left px-3 py-2">Month</th>
                  <th className="text-right px-3 py-2">Charges</th>
                  <th className="text-right px-3 py-2">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {monthlyAggregates.map((m) => (
                  <tr key={m.monthKey} className="border-t" data-testid={`row-month-agg-${m.monthKey}`}>
                    <td className="px-3 py-2 font-medium">{m.monthKey}</td>
                    <td className="px-3 py-2 text-right">{m.count}</td>
                    <td className="px-3 py-2 text-right text-green-600">${(m.totalCents / 100).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Vendor marketplace status */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h2 className="font-semibold text-base">Vendor Marketplace Status</h2>
            <p className="text-xs text-muted-foreground">Opt-in and listing status for all vendors.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {(["all", "opted-in", "listed"] as const).map((f) => (
              <Button
                key={f}
                size="sm"
                variant={statusFilter === f ? "default" : "outline"}
                onClick={() => setStatusFilter(f)}
                data-testid={`button-vendor-filter-${f}`}
              >
                {f === "all" ? "All" : f === "opted-in" ? "Opted In" : "Listed"}
              </Button>
            ))}
            <Button size="sm" variant="outline" onClick={() => vendorStatusQuery.refetch()} disabled={vendorStatusQuery.isFetching} data-testid="button-refresh-vendor-status">
              {vendorStatusQuery.isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
          </div>
        </div>
        <div className="relative mb-3">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Search by name or business name…"
            value={vendorSearch}
            onChange={(e) => setVendorSearch(e.target.value)}
            className="pl-8 h-8 text-sm"
            data-testid="input-vendor-search"
          />
        </div>
        {vendorStatusQuery.isLoading ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div>
        ) : filteredVendors.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center" data-testid="text-no-vendors">No vendors match the current filter.</div>
        ) : (
          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase">
                <tr>
                  <th className="text-left px-3 py-2">Vendor</th>
                  <th className="text-center px-3 py-2">Opted In</th>
                  <th className="text-center px-3 py-2">Listed</th>
                  <th className="text-left px-3 py-2">Joined</th>
                </tr>
              </thead>
              <tbody>
                {filteredVendors.map((v) => (
                  <tr key={v.id} className="border-t" data-testid={`row-vendor-marketplace-${v.id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium">{v.name}</div>
                      {v.businessName && <div className="text-xs text-muted-foreground">{v.businessName}</div>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {v.marketplaceOptedIn ? (
                        <Badge className="bg-green-100 text-green-700 border-green-200" data-testid={`badge-opted-in-${v.id}`}>Yes</Badge>
                      ) : (
                        <Badge variant="outline" className="text-muted-foreground" data-testid={`badge-opted-out-${v.id}`}>No</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {v.isMarketplaceListed ? (
                        <Badge className="bg-brand text-brand-foreground" data-testid={`badge-listed-${v.id}`}>Listed</Badge>
                      ) : (
                        <Badge variant="outline" className="text-muted-foreground" data-testid={`badge-not-listed-${v.id}`}>Not Listed</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {new Date(v.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Fee records */}
      <Card className="p-4" data-tour="admin-vendor-marketplace-fees-records">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h2 className="font-semibold text-base">Monthly Fee Records</h2>
            <p className="text-xs text-muted-foreground">All $19 marketplace charges — one per vendor per calendar month.</p>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            <Input
              placeholder="Search vendor or month…"
              value={feeSearch}
              onChange={(e) => setFeeSearch(e.target.value)}
              className="h-8 w-48 text-sm"
              data-testid="input-fee-search"
            />
            <Button size="sm" variant="outline" onClick={() => feesQuery.refetch()} disabled={feesQuery.isFetching} data-testid="button-refresh-fees">
              {feesQuery.isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
          </div>
        </div>
        {feesQuery.isLoading ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div>
        ) : filteredFees.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center" data-testid="text-no-fees">
            {feeSearch ? "No records match your search." : "No marketplace fee records yet."}
          </div>
        ) : (
          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase">
                <tr>
                  <th className="text-left px-3 py-2">Vendor</th>
                  <th className="text-left px-3 py-2">Month</th>
                  <th className="text-right px-3 py-2">Amount</th>
                  <th className="text-left px-3 py-2">Task</th>
                  <th className="text-left px-3 py-2">Payment Txn</th>
                  <th className="text-left px-3 py-2">Charged At</th>
                </tr>
              </thead>
              <tbody>
                {filteredFees.map((f) => (
                  <tr key={f.id} className="border-t" data-testid={`row-fee-${f.id}`}>
                    <td className="px-3 py-2 font-medium">{f.vendorName}</td>
                    <td className="px-3 py-2">{f.monthKey}</td>
                    <td className="px-3 py-2 text-right text-green-600">${(f.chargedCents / 100).toFixed(2)}</td>
                    <td className="px-3 py-2 text-xs" data-testid={`text-fee-task-${f.id}`}>
                      {f.taskTitle ? (
                        <span className="text-foreground">{f.taskTitle}</span>
                      ) : f.taskId ? (
                        <span className="text-muted-foreground">Task #{f.taskId}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs" data-testid={`text-fee-txn-${f.id}`}>
                      {f.stripeUrl ? (
                        <a
                          href={f.stripeUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-brand underline underline-offset-2 hover:text-brand-hover flex items-center gap-1"
                          data-testid={`link-fee-stripe-${f.id}`}
                        >
                          #{f.paymentTransactionId}
                          <ExternalLink className="h-3 w-3 inline shrink-0" />
                        </a>
                      ) : f.paymentTransactionId ? (
                        <span className="text-muted-foreground">#{f.paymentTransactionId}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {new Date(f.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
